import { ScriptsState } from '../modules/scripts/state'
import { CardOption, SchemeCardInfo } from '@/models/scripts'
import { CardOptionType } from '@/constants/scripts'

/**
 * Process cards list for the script scheme
 * - mark options to cards from previous levels as links to them
 * - add deep to the card
 *
 * @param {ScriptsState} state
 * @param {SchemeCardInfo[]} queue
 */
export const computeCardsDeepAndLinks = function(
  state: ScriptsState,
  rootNode: SchemeCardInfo,
) {
  if (state.scheme.root === null) throw `Tree is empty!`
  const queue: SchemeCardInfo[] = [rootNode]
  const deeps: Array<number> = [0]
  const visited = new Set()
  const linked = new Set()
  // for each node in queue
  let deep: undefined | number
  while (queue.length) {
    // - get card from queue
    const node = queue.shift()
    deep = deeps.shift()
    // const pair: undefined | { row: number; deep: number } = rows.shift()
    if (node === undefined || deep === undefined /* || pair === undefined */) {
      // actually this code wouldn't ever computed, but typescript needs this
      throw `Something went wrong`
    }
    // - go to current variant
    const content = node.content[node.current]
    if (!content) {
      continue
    }
    // - if we didn't visit this card
    if (visited.has(node.num)) {
      continue
    }
    content.deep = deep
    // - mark current node as visited
    visited.add(node.num)
    // - add deep level for next nodes
    deep++
    const options = content.options
    if (Array.isArray(options) && options.length > 0) {
      // - add current deep level to queue at first
      options.forEach((option: CardOption) => {
        // - if this option leads to the card
        if (
          option.type === CardOptionType.Card &&
          !option.collapsed &&
          option.link !== null
        ) {
          // - otherwise, find card by it's num in link option and add to queue
          const linkedCardId = state.scheme.numsIds.get(option.link)

          if (!linked.has(option.link) && rootNode.id !== linkedCardId) {
            option.isLink = false
            linked.add(option.link)
          } else {
            option.isLink = true
          }

          if (linkedCardId === undefined) {
            throw `Card with num ${option.link} not found.`
          }
          const nextCard = state.scheme.map[linkedCardId]
          option.nextCardId = nextCard.id

          queue.push(nextCard)
          deeps.push(deep as number)
        }
      })
    }
  }
  state.scheme.depth = deep ? deep : 0
}

export const computeWidesFromDeep = function(
  state: ScriptsState,
  node: SchemeCardInfo,
): number {
  const content = node.content[node.current]
  if (!content) {
    return 0
  }
  const options = content.options
  let wide = 0
  if (options.length) {
    let counter = 0
    options.forEach((option: CardOption) => {
      if (
        option.type === CardOptionType.Card &&
        option.link !== null &&
        !option.isLink &&
        !option.collapsed
      ) {
        const linkedCardId = state.scheme.numsIds.get(+option.link)
        if (linkedCardId === undefined) {
          throw `Card with num ${option.link} not found.`
        }
        const nextCard = state.scheme.map[linkedCardId]
        wide += computeWidesFromDeep(state, nextCard)
        if (counter > 0) {
          wide++
        }
        counter++
      }
    })
    content.wide = wide
  }
  return wide
}

function computeRows(state: ScriptsState, node: SchemeCardInfo, row: number) {
  // get card content
  const content = node.content[node.current]
  if (!content) {
    return row
  }
  // set card row
  content.row = row
  const options = content.options
  if (options.length) {
    // look through all options
    let printed = 0
    options.forEach((option: CardOption) => {
      // if it has a linked card and connection should be printed
      if (
        option.type === CardOptionType.Card &&
        option.link !== null &&
        !option.isLink &&
        !option.collapsed
      ) {
        // get linked card
        const linkedCardId = state.scheme.numsIds.get(+option.link)
        if (linkedCardId === undefined) {
          throw `Card with num ${option.link} not found`
        }
        const nextCard = state.scheme.map[linkedCardId]

        // if it isn't first card - increment row
        if (printed > 0) {
          row++
        }
        // go deeper to compute nextCard children rows
        row = computeRows(state, nextCard, row)
        // if nextCard has children and non zero wide
        const nextCardContent = nextCard.content[nextCard.current]
        nextCard.color = option.color
        if (nextCardContent !== undefined && nextCardContent.wide) {
          // add it to row counter
          row += nextCard.content[nextCard.current].wide
        }
        // count printed card
        printed++
      }
    })
  }
  return row
}

export const fillOptionAddons = function(option: CardOption) {
  option.link =
    typeof option.link === 'string' && option.link !== 'null'
      ? +option.link
      : option.link
  if (option.type === CardOptionType.Card) {
    option.collapsed = false
    option.isLink = false
  }
}

export const updateSchemeTree = function(state: ScriptsState): void {
  if (state.scheme.root === null) return
  const rootNode = state.scheme.map[state.scheme.root]
  computeCardsDeepAndLinks(state, rootNode)
  computeWidesFromDeep(state, rootNode)
  computeRows(state, rootNode, 0)
}
